import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { emailChangeConfirm } from '@/api/client'
import { AuthShell } from '@/components/auth-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/account/email-change/confirm/$token')({
  component: EmailChangeConfirmPage,
})

type Outcome = 'idle' | 'success' | 'invalid' | 'conflict' | 'error'

/**
 * TR-303 confirmation landing (US4). The path mirrors the backend 029 e-mail
 * template exactly: {FRONTEND_URL}/account/email-change/confirm/{raw_token}.
 * Works with no signed-in session (the token is the sole credential) and
 * finalizes with an explicit button POST — never on page load, so mail-client
 * link prefetchers cannot consume the one-time token (029 contract).
 *
 * Outcome mapping (029 owns the semantics — spec FR-022 adapted): the backend
 * deliberately answers every invalid token (unknown / expired / used /
 * cancelled) with the same 404 — no validity oracle — so the panel renders one
 * invalid-link outcome with re-initiation instructions; 409 is the separate
 * could-not-confirm outcome (address taken meanwhile / account inactive).
 */
function EmailChangeConfirmPage() {
  const { token } = Route.useParams()
  const [outcome, setOutcome] = useState<Outcome>('idle')
  const [submitting, setSubmitting] = useState(false)

  const confirm = async () => {
    setSubmitting(true)
    try {
      const response = await emailChangeConfirm(token)
      if (response.status === 200) setOutcome('success')
      else if (response.status === 404) setOutcome('invalid')
      else if (response.status === 409) setOutcome('conflict')
      else setOutcome('error')
    } catch {
      // network failure — retryable, nothing consumed server-side
      setOutcome('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Potwierdzenie zmiany adresu e-mail</CardTitle>
          {outcome === 'idle' && (
            <CardDescription>
              Kliknięcie poniższego przycisku zakończy zmianę adresu e-mail powiązanego z kontem.
              Nowy adres będzie od tej pory służył do logowania.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {outcome === 'idle' && (
            <Button onClick={confirm} disabled={submitting}>
              {submitting ? 'Potwierdzanie…' : 'Potwierdź zmianę adresu'}
            </Button>
          )}
          {outcome === 'success' && (
            <>
              <p className="text-sm text-green-700">
                Adres e-mail został zmieniony i powiązany z kontem.
              </p>
              <p className="text-sm text-muted-foreground">
                Od teraz do panelu logujesz się nowym adresem. Twoje aktywne sesje pozostały bez
                zmian.
              </p>
              <Button asChild variant="outline" className="self-start">
                <Link to="/login">Przejdź do logowania</Link>
              </Button>
            </>
          )}
          {outcome === 'invalid' && (
            <p className="text-sm">
              Link jest nieważny — mógł wygasnąć lub zostać już użyty. Aby zmienić adres, rozpocznij
              zmianę od nowa w sekcji „Adres e-mail konta" swojego profilu.
            </p>
          )}
          {outcome === 'conflict' && (
            <p className="text-sm">
              Nie udało się potwierdzić zmiany. Rozpocznij zmianę adresu od nowa w sekcji „Adres
              e-mail konta" swojego profilu.
            </p>
          )}
          {outcome === 'error' && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-destructive">
                Wystąpił błąd podczas potwierdzania. Spróbuj ponownie.
              </p>
              <Button onClick={confirm} disabled={submitting} className="self-start">
                Spróbuj ponownie
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  )
}
